"use client";

import { useState } from "react";
import Image from "next/image";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";

type Props = {
  /** The Bunny embed URL as the admin saved it. */
  embedUrl: string;
  /** The course's Cloudflare hero image, the poster until someone presses play. */
  posterUrl: string | null;
  title: string;
  playLabel: string;
  playAria: string;
};

/**
 * `SITE-SPD01`: the course preview video loads when a visitor asks for it.
 *
 * The iframe used to sit in the hero from the first paint, and the saved embed
 * URL carries `preload=true`, so every visit downloaded the Bunny player
 * (about 510 KB of script) and the first video segments (about 520 KB) before
 * anyone pressed play: the course page's LCP was 5.4 s on a mid-range phone.
 * Now the hero shows the course image and a play button, and the button swaps
 * in the player with sound and autoplay on, which the press itself permits.
 */
export function CourseHeroVideo({ embedUrl, posterUrl, title, playLabel, playAria }: Props) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <iframe
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full border-0"
        src={playerUrl(embedUrl)}
        title={title}
      />
    );
  }

  return (
    // `SITE-CRS01`: the play pill sits in the poster's bottom-left corner; centred,
    // it covered the course title the poster itself prints.
    <Button
      type="button"
      variant={null}
      size={null}
      onClick={() => setPlaying(true)}
      aria-label={playAria}
      className="group absolute inset-0 h-full w-full items-end justify-start rounded-none p-3 focus-visible:ring-offset-2"
    >
      {posterUrl ? (
        <Image src={posterUrl} alt="" fill priority className="object-cover" sizes="(max-width: 1024px) 100vw, 45vw" />
      ) : (
        <span className="absolute inset-0 bg-neutral-900" aria-hidden />
      )}
      <span className="absolute inset-0 bg-neutral-950/10 transition-colors duration-200 group-hover:bg-neutral-950/25" aria-hidden />
      <span className="relative inline-flex items-center gap-2 rounded-full bg-white py-1.5 pl-1.5 pr-4 text-[13px] font-bold text-neutral-900 shadow-card transition-transform duration-200 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
          <Play className="h-3.5 w-3.5 translate-x-px fill-current" aria-hidden />
        </span>
        {playLabel}
      </span>
    </Button>
  );
}

/** The saved URL with the player told to start now, with sound, and to load what it needs. */
export function playerUrl(embedUrl: string): string {
  try {
    const url = new URL(embedUrl);
    url.searchParams.set("autoplay", "true");
    url.searchParams.set("muted", "false");
    url.searchParams.set("preload", "true");
    return url.toString();
  } catch {
    return embedUrl;
  }
}
